import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import './HREmployees.css';

const CITIES = ['All Cities', 'Lahore', 'Karachi', 'Islamabad', 'Multan'];
const ROLES  = ['employee', 'hr', 'admin'];

const EMPTY_FORM = {
  employee_id: '', full_name: '', phone: '', email: '',
  department_id: '', job_title: '', location_id: '', shift_id: '1',
  base_salary: '', joining_date: '', tenure_years: '', role: 'employee',
  temp_password: '',
};

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

// Calculate tenure in years (1 decimal place) from a joining date string
function calculateTenure(joiningDateStr) {
  if (!joiningDateStr) return '';
  const joinDate = new Date(joiningDateStr);
  const now = new Date();
  if (isNaN(joinDate.getTime())) return '';
  const diffMs = now - joinDate;
  if (diffMs < 0) return '0'; // future date
  const years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  return years.toFixed(1);
}

function getAvatarClass(name) {
  const c = ['emp-av-blue','emp-av-teal','emp-av-amber','emp-av-purple','emp-av-coral','emp-av-green'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return c[h % c.length];
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPKR(amount) {
  if (!amount && amount !== 0) return '—';
  return `PKR ${Number(amount).toLocaleString('en-PK')}`;
}

// ─── ADD / EDIT MODAL ────────────────────────────────────────────────────────

function EmployeeModal({ mode, employee, locations, departments, shifts, onSave, onClose }) {
  const [form, setForm] = useState(
    mode === 'edit' && employee
      ? {
          employee_id:   employee.employee_id   || '',
          full_name:     employee.full_name     || '',
          phone:         employee.phone         || '',
          email:         employee.email         || '',
          department_id: employee.department_id || '',
          job_title:     employee.job_title     || '',
          location_id:   employee.location_id   || '',
          shift_id:      employee.shift_id      || '1',
          base_salary:   employee.base_salary   || '',
          joining_date:  employee.joining_date ? employee.joining_date.split('T')[0] : '',
          tenure_years:  employee.tenure_years  || '',
          role:          employee.role          || 'employee',
          temp_password: '',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Auto-suggest employee ID when location changes
  const handleLocationChange = (locId) => {
    set('location_id', locId);
    if (mode === 'add' && !form.employee_id) {
      const loc = locations.find(l => String(l.id) === String(locId));
      if (loc?.emp_prefix) {
        set('employee_id', `${loc.emp_prefix}-`);
      }
    }
  };

  const handleSave = async () => {
    if (!form.full_name.trim())   { setError('Full name is required'); return; }
    if (mode === 'add' && !form.employee_id.trim()) { setError('Employee ID is required'); return; }
    if (!form.location_id)        { setError('Location is required'); return; }

    setSaving(true); setError('');
    try {
      if (mode === 'add') {
        const res = await api.createEmployee({
          ...form,
          employee_id:   form.employee_id.toUpperCase(),
          base_salary:   form.base_salary ? parseFloat(form.base_salary) : 0,
          shift_id:      parseInt(form.shift_id),
          location_id:   parseInt(form.location_id),
          department_id: form.department_id ? parseInt(form.department_id) : null,
          tenure_years:  form.tenure_years || 0,
        });
        if (res.error) { setError(res.error); return; }
      } else {
        const res = await api.updateEmployee(employee.id, {
          full_name:     form.full_name,
          phone:         form.phone,
          email:         form.email,
          department_id: form.department_id ? parseInt(form.department_id) : null,
          job_title:     form.job_title,
          location_id:   parseInt(form.location_id),
          shift_id:      parseInt(form.shift_id),
          base_salary:   form.base_salary ? parseFloat(form.base_salary) : 0,
          role:          form.role,
        });
        if (res.error) { setError(res.error); return; }
      }
      onSave();
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get prefix hint for selected location
  const selectedLoc = locations.find(l => String(l.id) === String(form.location_id));
  const prefixHint  = selectedLoc?.emp_prefix;

  return (
    <div className="emp-modal-overlay" onClick={onClose}>
      <div className="emp-modal" onClick={e => e.stopPropagation()}>
        <div className="emp-modal-header">
          <h2>{mode === 'add' ? 'Add New Employee' : 'Edit Employee'}</h2>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="emp-modal-error">{error}</div>}

        <div className="emp-modal-body">

          {/* Assignment FIRST so prefix auto-fills */}
          <div className="emp-form-section">Assignment</div>
          <div className="emp-form-grid">
            <div className="emp-form-group">
              <label>Location *</label>
              <select value={form.location_id} onChange={e => handleLocationChange(e.target.value)}>
                <option value="">Select location</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.emp_prefix ? `[${l.emp_prefix}] ` : ''}{l.name} — {l.city}
                  </option>
                ))}
              </select>
              {prefixHint && (
                <div className="emp-prefix-hint">
                  Employee ID prefix for this location: <strong>{prefixHint}-XXX</strong>
                </div>
              )}
            </div>
            <div className="emp-form-group">
              <label>Shift</label>
              <select value={form.shift_id} onChange={e => set('shift_id', e.target.value)}>
                {shifts.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="emp-form-group">
              <label>Role</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                {ROLES.map(r => (
                  <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="emp-form-section">Personal Info</div>
          <div className="emp-form-grid">
            {mode === 'add' && (
              <div className="emp-form-group">
                <label>Employee ID *</label>
                <input type="text"
                  placeholder={prefixHint ? `e.g. ${prefixHint}-001` : 'e.g. BKO-001'}
                  value={form.employee_id}
                  onChange={e => set('employee_id', e.target.value.toUpperCase())} />
              </div>
            )}
            <div className="emp-form-group">
              <label>Full Name *</label>
              <input type="text" placeholder="Full name"
                value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Phone</label>
              <input type="text" placeholder="e.g. 0300-1234567"
                value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Email</label>
              <input type="email" placeholder="email@example.com"
                value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="emp-form-group">
              <label>Department</label>
              <select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
                <option value="">Select department</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="emp-form-group">
              <label>Job Title</label>
              <input type="text" placeholder="e.g. Sales Executive"
                value={form.job_title} onChange={e => set('job_title', e.target.value)} />
            </div>
          </div>

          <div className="emp-form-section">
            Salary & Dates
            <span className="emp-form-optional"> (all optional)</span>
          </div>
          <div className="emp-form-grid">
            <div className="emp-form-group">
              <label>Base Salary (PKR) <span className="emp-optional-tag">optional</span></label>
              <input type="number" placeholder="Can be added later"
                value={form.base_salary} onChange={e => set('base_salary', e.target.value)} />
            </div>
            {mode === 'add' && (
              <>
                <div className="emp-form-group">
                  <label>Joining Date <span className="emp-optional-tag">optional</span></label>
                  <input type="date"
                    value={form.joining_date}
                    onChange={e => {
                      const date = e.target.value;
                      setForm(f => ({
                        ...f,
                        joining_date: date,
                        tenure_years: date ? calculateTenure(date) : f.tenure_years,
                      }));
                    }} />
                </div>
                <div className="emp-form-group">
                  <label>Tenure (years) <span className="emp-optional-tag">auto-calculated</span></label>
                  <input type="number" step="0.1" placeholder="Set joining date first"
                    value={form.tenure_years}
                    onChange={e => set('tenure_years', e.target.value)}
                    readOnly={!!form.joining_date}
                    className={form.joining_date ? 'emp-input-readonly' : ''}
                    title={form.joining_date ? 'Auto-calculated from joining date' : ''} />
                  {form.joining_date && (
                    <div className="emp-tenure-hint">
                      Auto-calculated from joining date
                    </div>
                  )}
                </div>
                <div className="emp-form-group">
                  <label>Temporary Password <span className="emp-optional-tag">optional</span></label>
                  <input type="text" placeholder="Leave blank to set later"
                    value={form.temp_password} onChange={e => set('temp_password', e.target.value)} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="emp-modal-footer">
          <button className="emp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="emp-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'add' ? 'Add Employee' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DEACTIVATE CONFIRM ───────────────────────────────────────────────────────

function DeactivateModal({ employee, onConfirm, onClose }) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };
  return (
    <div className="emp-modal-overlay" onClick={onClose}>
      <div className="emp-modal emp-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="emp-modal-header">
          <h2>Deactivate Employee</h2>
          <button className="emp-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="emp-modal-body">
          <p className="emp-deactivate-msg">
            Are you sure you want to deactivate <strong>{employee?.full_name}</strong>?
            They will no longer be able to log in or clock in.
          </p>
        </div>
        <div className="emp-modal-footer">
          <button className="emp-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="emp-btn-danger" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Deactivating…' : 'Yes, Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────

function EmployeeDetailPanel({ employeeId, onClose, onEdit, onDeactivate, onResetDevice }) {
  const [employee, setEmployee] = useState(null);
  const [salary, setSalary]     = useState(null);
  const [loading, setLoading]   = useState(true);

  const now   = new Date();
  const month = now.getMonth() + 1;
  const year  = now.getFullYear();

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    api.getEmployee(employeeId).then(async emp => {
      setEmployee(emp);
      if (emp?.employee_id) {
        const sal = await api.calculateSalary(emp.employee_id, month, year);
        setSalary(sal);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [employeeId]);

  if (!employeeId) return null;

  return (
    <div className="emp-detail-panel">
      <div className="emp-detail-header">
        <button className="emp-detail-close" onClick={onClose}>✕</button>
        <h3>Employee Profile</h3>
        {employee && (
          <div className="emp-detail-actions">
            <button className="emp-detail-edit-btn" onClick={() => onEdit(employee)}>Edit</button>
            {employee.role === 'employee' && (
              <button className="emp-detail-device-btn" onClick={() => onResetDevice(employee.id, employee.full_name)}>
                📱 Reset Device
              </button>
            )}
            {employee.is_active && (
              <button className="emp-detail-deact-btn" onClick={() => onDeactivate(employee)}>
                Deactivate
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="emp-detail-loading"><div className="lb-spinner" /></div>
      ) : !employee ? (
        <div className="emp-detail-loading">Failed to load employee.</div>
      ) : (
        <div className="emp-detail-body">
          <div className="emp-detail-hero">
            <div className={`emp-detail-avatar ${getAvatarClass(employee.full_name)}`}>
              {getInitials(employee.full_name)}
            </div>
            <div className="emp-detail-hero-info">
              <span className="emp-detail-name">{employee.full_name}</span>
              <span className="emp-detail-code">{employee.employee_id}</span>
              <span className={`emp-detail-status-badge ${employee.is_active ? 'active' : 'inactive'}`}>
                {employee.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="emp-detail-section">Employee Details</div>
          {[
            { label: 'Phone',       val: employee.phone       || '—' },
            { label: 'Email',       val: employee.email       || '—' },
            { label: 'Department',  val: employee.department_name || employee.department || '—' },
            { label: 'Job Title',   val: employee.job_title   || '—' },
            { label: 'Role',        val: employee.role        || '—' },
            { label: 'Location',    val: employee.location_name || '—' },
            { label: 'City',        val: employee.city        || '—' },
            { label: 'Shift',       val: employee.shift_name  || '—' },
            { label: 'Joining Date',val: formatDate(employee.joining_date) },
            { label: 'Base Salary', val: employee.base_salary ? formatPKR(employee.base_salary) : '—' },
          ].map((r, i) => (
            <div key={i} className="emp-detail-row">
              <span className="emp-detail-lbl">{r.label}</span>
              <span className="emp-detail-val">{r.val}</span>
            </div>
          ))}

          <div className="emp-detail-section" style={{ marginTop: 20 }}>
            {now.toLocaleString('default', { month: 'long' })} {year} — Summary
          </div>
          {salary && !salary.error ? (
            <>
              {[
                { label: 'Deduction %',     val: `${salary.deduction_percent ?? '0.00'}%`, color: parseFloat(salary.deduction_percent) > 0 ? 'red' : 'green' },
                { label: 'Days Present',    val: salary.working_days_present ?? '—', color: 'green' },
                { label: 'Days Absent',     val: salary.absent_days ?? '—',          color: salary.absent_days > 0 ? 'red' : '' },
                { label: 'Late Count',      val: salary.late_count ?? '—',           color: salary.late_count > 0 ? 'amber' : '' },
                { label: 'Casual Used',     val: salary.casual_leaves_consumed ?? '—' },
                { label: 'Auto Casual',     val: salary.casual_auto_days ?? '—' },
                { label: 'Approved Leaves', val: salary.approved_leaves ?? '—' },
              ].map((r, i) => (
                <div key={i} className="emp-detail-row">
                  <span className="emp-detail-lbl">{r.label}</span>
                  <span className={`emp-detail-val ${r.color ? `emp-val-${r.color}` : ''}`}>{r.val}</span>
                </div>
              ))}
              {salary.has_salary ? (
                <>
                  <div className="emp-detail-row">
                    <span className="emp-detail-lbl">Total Deduction</span>
                    <span className="emp-detail-val emp-val-red">{formatPKR(salary.total_deduction)}</span>
                  </div>
                  <div className="emp-detail-row">
                    <span className="emp-detail-lbl">Net Salary</span>
                    <span className="emp-detail-val emp-val-green">{formatPKR(salary.net_salary)}</span>
                  </div>
                </>
              ) : (
                <div className="emp-detail-no-salary-set">
                  💡 Salary not set — add base salary to see PKR amounts
                </div>
              )}
            </>
          ) : (
            <div className="emp-detail-no-salary">No salary data for this month yet.</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function HREmployees() {
  const [employees, setEmployees]     = useState([]);
  const [locations, setLocations]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter]   = useState('All Cities');
  const [deptFilter, setDeptFilter]   = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedId, setSelectedId]   = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [deactivateEmployee, setDeactivateEmployee] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, locRes, deptRes, shiftRes] = await Promise.all([
        api.getAllEmployees(),
        api.getAllLocations(),
        api.getAllDepartments(),
        fetch('https://fotileams.com/api/shifts', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        }).then(r => r.json()).catch(() => ({ shifts: [] })),
      ]);
      setEmployees(empRes.employees || []);
      setLocations(locRes.locations || []);
      setDepartments(deptRes.departments || []);
      setShifts(shiftRes.shifts || []);
      setError(null);
    } catch (err) {
      setError('Failed to load employees.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  let filtered = [...employees];
  if (statusFilter === 'active')   filtered = filtered.filter(e => e.is_active);
  if (statusFilter === 'inactive') filtered = filtered.filter(e => !e.is_active);
  if (cityFilter !== 'All Cities') filtered = filtered.filter(e => e.city === cityFilter);
  if (deptFilter !== 'all')        filtered = filtered.filter(e => String(e.department_id) === deptFilter);
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(e =>
      (e.full_name     || '').toLowerCase().includes(q) ||
      (e.employee_id   || '').toLowerCase().includes(q) ||
      (e.department_name || e.department || '').toLowerCase().includes(q) ||
      (e.location_name || '').toLowerCase().includes(q)
    );
  }

  const handleResetDevice = async (employeeId, employeeName) => {
    if (!window.confirm(`Reset device lock for ${employeeName}?\n\nThey'll be able to register a new device on their next login.`)) return;
    try {
      await fetch(`https://fotileams.com/api/employees/${employeeId}/reset-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      alert('Device lock reset successfully');
      fetchData();
    } catch {
      alert('Failed to reset device');
    }
  };

  const handleDeactivateConfirm = async () => {
    await api.deactivateEmployee(deactivateEmployee.id);
    setDeactivateEmployee(null);
    setSelectedId(null);
    fetchData();
  };

  const totalActive   = employees.filter(e => e.is_active).length;
  const totalInactive = employees.filter(e => !e.is_active).length;

  // Use fetched shifts or fallback
  const shiftOptions = shifts.length > 0 ? shifts : [
    { id: 1, name: 'Slot 1 — 9AM to 6PM' },
    { id: 2, name: 'Slot 2 — 10AM to 6PM' },
    { id: 3, name: 'Slot 3 — 10AM to 7PM' },
    { id: 4, name: 'Slot 4 — 11AM to 8PM' },
  ];

  return (
    <div className="emp-root">
      <div className="emp-header">
        <div className="emp-header-left">
          <h1 className="emp-title">Employees</h1>
          <span className="emp-subtitle">{employees.length} total · {totalActive} active</span>
        </div>
        <button className="emp-add-btn" onClick={() => setShowAddModal(true)}>
          + Add Employee
        </button>
      </div>

      <div className="emp-stats">
        <div className="emp-stat-card">
          <span className="emp-stat-num">{employees.length}</span>
          <span className="emp-stat-lbl">Total</span>
        </div>
        <div className="emp-stat-card emp-stat-green">
          <span className="emp-stat-num">{totalActive}</span>
          <span className="emp-stat-lbl">Active</span>
        </div>
        <div className="emp-stat-card emp-stat-red">
          <span className="emp-stat-num">{totalInactive}</span>
          <span className="emp-stat-lbl">Inactive</span>
        </div>
        {departments.map(dept => (
          <div key={dept.id} className="emp-stat-card emp-stat-city">
            <span className="emp-stat-num">
              {employees.filter(e => e.department_id === dept.id && e.is_active).length}
            </span>
            <span className="emp-stat-lbl">{dept.name}</span>
          </div>
        ))}
      </div>

      <div className="emp-filters">
        <input className="emp-search" type="text"
          placeholder="Search by name, ID, department, location…"
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        <div className="emp-filter-group">
          {CITIES.map(c => (
            <button key={c}
              className={`emp-filter-btn ${cityFilter === c ? 'emp-filter-active' : ''}`}
              onClick={() => setCityFilter(c)}>{c}</button>
          ))}
        </div>
        <div className="emp-filter-group">
          <button className={`emp-filter-btn ${deptFilter === 'all' ? 'emp-filter-active' : ''}`}
            onClick={() => setDeptFilter('all')}>All Depts</button>
          {departments.map(d => (
            <button key={d.id}
              className={`emp-filter-btn ${deptFilter === String(d.id) ? 'emp-filter-active' : ''}`}
              onClick={() => setDeptFilter(String(d.id))}>{d.name}</button>
          ))}
        </div>
        <div className="emp-filter-group">
          {[
            { key: 'active',   label: '● Active' },
            { key: 'inactive', label: '○ Inactive' },
            { key: 'all',      label: 'All' },
          ].map(s => (
            <button key={s.key}
              className={`emp-filter-btn ${statusFilter === s.key ? 'emp-filter-active' : ''}`}
              onClick={() => setStatusFilter(s.key)}>{s.label}</button>
          ))}
        </div>
      </div>

      <div className={`emp-content ${selectedId ? 'emp-content-split' : ''}`}>
        <div className="emp-table-wrap">
          {error && <div className="emp-error">{error}</div>}
          {loading ? (
            <div className="emp-loading">
              <div className="lb-spinner" />
              <p>Loading employees…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="emp-empty">No employees match your filters.</div>
          ) : (
            <table className="emp-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Shift</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => (
                  <tr key={emp.id}
                    className={`emp-row ${selectedId === emp.id ? 'emp-row-selected' : ''} ${!emp.is_active ? 'emp-row-inactive' : ''}`}
                    onClick={() => setSelectedId(selectedId === emp.id ? null : emp.id)}
                  >
                    <td>
                      <div className="lb-emp-cell">
                        <div className={`lb-avatar ${getAvatarClass(emp.full_name)}`}>
                          {getInitials(emp.full_name)}
                        </div>
                        <div className="lb-emp-info">
                          <span className="lb-emp-name">{emp.full_name}</span>
                          <span className="lb-emp-code">{emp.employee_id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="emp-dept-cell">
                        <span className="emp-dept">{emp.department_name || emp.department || '—'}</span>
                        <span className="emp-title-small">{emp.job_title || ''}</span>
                      </div>
                    </td>
                    <td>
                      <div className="lb-loc-cell">
                        <span className="lb-loc-name">{emp.location_name || '—'}</span>
                        <span className="lb-city-tag">{emp.city || ''}</span>
                      </div>
                    </td>
                    <td><span className="lb-shift-tag">{emp.shift_name || '—'}</span></td>
                    <td>
                      <span className="emp-salary">
                        {emp.base_salary ? formatPKR(emp.base_salary) : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`emp-status-badge ${emp.is_active ? 'emp-badge-active' : 'emp-badge-inactive'}`}>
                        {emp.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="emp-action-btns">
                        <button className="emp-action-edit" onClick={() => setEditEmployee(emp)}>Edit</button>
                        {emp.is_active && (
                          <button className="emp-action-deact" onClick={() => setDeactivateEmployee(emp)}>
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="emp-footer">
            Showing {filtered.length} of {employees.length} employees
          </div>
        </div>

        {selectedId && (
          <EmployeeDetailPanel
            employeeId={selectedId}
            onClose={() => setSelectedId(null)}
            onEdit={(emp) => setEditEmployee(emp)}
            onDeactivate={(emp) => setDeactivateEmployee(emp)}
            onResetDevice={handleResetDevice}
          />
        )}
      </div>

      {showAddModal && (
        <EmployeeModal mode="add"
          locations={locations} departments={departments} shifts={shiftOptions}
          onSave={() => { setShowAddModal(false); fetchData(); }}
          onClose={() => setShowAddModal(false)} />
      )}

      {editEmployee && (
        <EmployeeModal mode="edit"
          employee={editEmployee}
          locations={locations} departments={departments} shifts={shiftOptions}
          onSave={() => { setEditEmployee(null); fetchData(); }}
          onClose={() => setEditEmployee(null)} />
      )}

      {deactivateEmployee && (
        <DeactivateModal
          employee={deactivateEmployee}
          onConfirm={handleDeactivateConfirm}
          onClose={() => setDeactivateEmployee(null)} />
      )}
    </div>
  );
}